import { useCallback, useMemo, useState } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useResultsEditing } from './useResultsEditing';
import type { ResultsGridEditMode } from '../../common/components/ResultsGrid';
import type { QueryColumn } from '../../../../types/queryExecution';
import type { DatabaseEngineId } from '../../../../core/engines';

type UseResultsEditModeParams = {
  tabId: string;
  engineId: DatabaseEngineId;
  connectionId: string | undefined;
  database: string | undefined;
  connectionInfo:
    | { connectionId?: string; connectionName?: string; serverHost?: string; database?: string }
    | undefined;
  activeResultSetColumns: QueryColumn[] | undefined;
  isWindowed: boolean;
  onSaveComplete: () => void;
  /** The tab's transaction auto-begin for a Save (see `useResultsEditing`). */
  prepareForSave: () => Promise<boolean>;
};

/**
 * Results-grid edit mode for a query editor tab: the results-editing hook
 * wiring, the stable `editMode` prop object for ResultsGrid (including the
 * windowed-mode disabled pencil), the edit-preview dialog state and the
 * "Open in Query Editor" action. Behavior carried verbatim from the previous
 * inline implementation. (The grid's Add to WHERE is `useResultsAddToWhere`.)
 */
export function useResultsEditMode({
  tabId,
  engineId,
  connectionId,
  database,
  connectionInfo,
  activeResultSetColumns,
  isWindowed,
  onSaveComplete,
  prepareForSave,
}: UseResultsEditModeParams) {
  // Stable reference for empty columns — avoids creating a new [] on every render,
  // which would cause an infinite re-render loop in useResultsEditing's useEffect.
  const resultsColumns = useMemo(() => activeResultSetColumns ?? [], [activeResultSetColumns]);

  // Results editing hook
  const resultsEditing = useResultsEditing({
    columns: resultsColumns,
    connectionId,
    database,
    tabId,
    engine: engineId,
    onSaveComplete,
    prepareForSave,
  });

  // Preview dialog state
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

  const handleOpenPreviewDialog = useCallback(() => {
    if (resultsEditing.pendingChangeCount > 0) {
      setIsPreviewDialogOpen(true);
    }
  }, [resultsEditing.pendingChangeCount]);

  const handleOpenInQueryEditor = useCallback((sql: string) => {
    const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
    const queryNumber = getNextQueryNumber();
    addTab({
      title: `Query-${queryNumber}`,
      type: 'query',
      content: { query: sql },
      connectionInfo: connectionInfo ? {
        connectionId: connectionInfo.connectionId,
        connectionName: connectionInfo.connectionName,
        serverHost: connectionInfo.serverHost,
        database: connectionInfo.database,
      } : undefined,
    });
  }, [connectionInfo]);

  // Build editMode prop for ResultsGrid (always for non-sqlite engines)
  // Important: keep this object stable so ResultsGrid can avoid re-rendering while typing in the editor.
  const editModeProp: ResultsGridEditMode | undefined = useMemo(() => {
    if (engineId === 'sqlite') return undefined;
    // In windowed mode, surface a visible-but-disabled pencil explaining why
    // editing is off — instead of silently hiding the button. Keeps the
    // toolbar layout consistent between small and large results.
    if (isWindowed) {
      return {
        hasEditableColumns: false,
        readOnlyReason:
          'Edit is available for smaller results (under 50,000 rows and 20 MB). Narrow the query to enable editing.',
        isEditMode: false,
        pendingChangeCount: 0,
        isSaving: false,
        isColumnEditable: () => false,
        getCellPendingValue: () => undefined,
        getPrimaryKeyForCell: () => null,
        onToggleEditMode: () => { /* disabled */ },
        onSave: () => { /* disabled */ },
        onDiscard: () => { /* disabled */ },
        onCellEdit: () => { /* disabled */ },
      };
    }
    return {
      hasEditableColumns: resultsEditing.hasEditableColumns,
      readOnlyReason: resultsEditing.readOnlyReason,
      isEditMode: resultsEditing.isEditMode,
      pendingChangeCount: resultsEditing.pendingChangeCount,
      isSaving: resultsEditing.isSaving,
      isColumnEditable: resultsEditing.isColumnEditable,
      getCellPendingValue: resultsEditing.getCellPendingValue,
      getPrimaryKeyForCell: resultsEditing.getPrimaryKeyForCell,
      onToggleEditMode: resultsEditing.toggleEditMode,
      onSave: resultsEditing.saveChanges,
      onDiscard: resultsEditing.discardChanges,
      onCellEdit: resultsEditing.addCellUpdate,
      onPreview: handleOpenPreviewDialog,
    };
  }, [
    engineId,
    isWindowed,
    handleOpenPreviewDialog,
    resultsEditing.addCellUpdate,
    resultsEditing.discardChanges,
    resultsEditing.getCellPendingValue,
    resultsEditing.getPrimaryKeyForCell,
    resultsEditing.hasEditableColumns,
    resultsEditing.isColumnEditable,
    resultsEditing.isEditMode,
    resultsEditing.isSaving,
    resultsEditing.pendingChangeCount,
    resultsEditing.readOnlyReason,
    resultsEditing.saveChanges,
    resultsEditing.toggleEditMode,
  ]);

  return {
    resultsEditing,
    editModeProp,
    isPreviewDialogOpen,
    setIsPreviewDialogOpen,
    handleOpenInQueryEditor,
  };
}
